import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Send, Trash2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const AdminSuporteNovo = () => {
  const navigate = useNavigate();
  const [medicos, setMedicos] = useState([]);
  const [medicoSelecionado, setMedicoSelecionado] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [resposta, setResposta] = useState('');
  const [enviandoResposta, setEnviandoResposta] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    fetchMedicos();
    const interval = setInterval(fetchMedicos, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (medicoSelecionado) {
      fetchMensagens(medicoSelecionado.usuarioId);
      marcarComoLidas(medicoSelecionado.usuarioId);
    }
  }, [medicoSelecionado]);

  const fetchMedicos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/mensagens-suporte/medicos-lista`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMedicos(data);
      } else {
        toast.error('Erro ao carregar lista de médicos');
      }
    } catch (error) {
      console.error('Erro ao buscar médicos:', error);
      toast.error('Erro ao carregar médicos');
    } finally {
      setLoading(false);
    }
  };

  const fetchMensagens = async (usuarioId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/mensagens-suporte/admin`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const mensagensDoUsuario = data.filter(msg => msg.usuarioId === usuarioId);
        setMensagens(mensagensDoUsuario);
      }
    } catch (error) {
      console.error('Erro ao buscar mensagens:', error);
    }
  };

  const marcarComoLidas = async (usuarioId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${backendUrl}/api/mensagens-suporte/marcar-lidas/${usuarioId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      // Refresh lista de médicos para atualizar contadores
      fetchMedicos();
    } catch (error) {
      console.error('Erro ao marcar como lidas:', error);
    }
  };

  const handleEnviarResposta = async (mensagemId) => {
    if (!resposta.trim()) {
      toast.error('Por favor, digite uma resposta');
      return;
    }

    setEnviandoResposta(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/mensagens-suporte/${mensagemId}/responder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ mensagem: resposta })
      });

      if (response.ok) {
        toast.success('Resposta enviada com sucesso');
        setResposta('');
        fetchMensagens(medicoSelecionado.usuarioId);
      } else {
        toast.error('Erro ao enviar resposta');
      }
    } catch (error) {
      console.error('Erro ao enviar resposta:', error);
      toast.error('Erro ao enviar resposta');
    } finally {
      setEnviandoResposta(false);
    }
  };

  const handleLimparHistorico = async () => {
    if (!window.confirm(`Tem certeza que deseja limpar todo o histórico de ${medicoSelecionado.usuarioNome}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/mensagens-suporte/usuario/${medicoSelecionado.usuarioId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Histórico limpo com sucesso');
        setMedicoSelecionado(null);
        setMensagens([]);
        fetchMedicos();
      } else {
        toast.error('Erro ao limpar histórico');
      }
    } catch (error) {
      console.error('Erro ao limpar histórico:', error);
      toast.error('Erro ao limpar histórico');
    }
  };

  const formatarData = (dataString) => {
    const data = new Date(dataString);
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleImageClick = (imageUrl) => {
    console.log('[ADMIN SUPORTE] Abrindo imagem:', imageUrl);
    console.log('[ADMIN SUPORTE] Backend URL:', backendUrl);
    setSelectedImage(imageUrl);
    setImageModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#191919] flex items-center justify-center">
        <p className="text-white">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#191919] text-white">
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin')}
            className="mb-4 border-gray-600 text-white hover:bg-gray-800 hover:text-white"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar ao Dashboard
          </Button>
          <h1 className="text-3xl font-bold">Mensagens de Suporte</h1>
          <p className="text-gray-400 mt-2">Gerencie as mensagens dos médicos</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista de Médicos */}
          <div className="lg:col-span-1">
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Médicos</CardTitle>
              </CardHeader>
              <CardContent>
                {medicos.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">Nenhuma mensagem ainda</p>
                ) : (
                  <div className="space-y-2">
                    {medicos.map((medico) => (
                      <div
                        key={medico.usuarioId}
                        onClick={() => setMedicoSelecionado(medico)}
                        className={`p-4 rounded-lg cursor-pointer transition-colors ${
                          medicoSelecionado?.usuarioId === medico.usuarioId
                            ? 'bg-[#CF0D1D] text-white'
                            : 'bg-[#252525] hover:bg-[#2a2a2a] text-gray-300'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{medico.usuarioNome}</h3>
                              {medico.naoLidas > 0 && (
                                <span className="bg-white text-[#CF0D1D] text-xs font-bold rounded-full px-2 py-0.5">
                                  {medico.naoLidas}
                                </span>
                              )}
                            </div>
                            <p className="text-sm opacity-75 mt-1 truncate">
                              {medico.ultimaMensagem}
                            </p>
                            <p className="text-xs opacity-50 mt-1">
                              {formatarData(medico.ultimaData)}
                            </p>
                          </div>
                          <MessageSquare className="w-5 h-5 flex-shrink-0 ml-2" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Painel de Conversa */}
          <div className="lg:col-span-2">
            {!medicoSelecionado ? (
              <Card className="bg-[#1a1a1a] border-gray-800">
                <CardContent className="py-12 text-center">
                  <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Selecione um médico para ver as mensagens</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[#1a1a1a] border-gray-800">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="text-white">{medicoSelecionado.usuarioNome}</CardTitle>
                      <p className="text-sm text-gray-400">{medicoSelecionado.usuarioEmail}</p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleLimparHistorico}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Limpar Histórico
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Mensagens */}
                  <div className="space-y-4 mb-6 max-h-[500px] overflow-y-auto">
                    {mensagens.map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-4 rounded-lg ${
                          msg.respondidoPorAdmin
                            ? 'bg-[#252525] border-l-4 border-[#CF0D1D]'
                            : 'bg-[#2a2a2a]'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-semibold text-white">
                            {msg.respondidoPorAdmin ? '📧 Você (Admin)' : '👤 ' + msg.usuarioNome}
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatarData(msg.criadoEm)}
                          </span>
                        </div>
                        <p className="text-gray-300 whitespace-pre-wrap">{msg.mensagem}</p>
                        {msg.screenshotUrl && (
                          <div className="mt-3">
                            <img
                              src={`${backendUrl}${msg.screenshotUrl}`}
                              alt="Screenshot"
                              className="max-w-full max-h-60 rounded-lg border border-gray-700 cursor-pointer hover:opacity-80 transition-opacity object-contain"
                              onClick={() => handleImageClick(`${backendUrl}${msg.screenshotUrl}`)}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Formulário de Resposta */}
                  <div className="border-t border-gray-800 pt-4">
                    <label className="text-sm font-medium text-gray-300 mb-2 block">
                      Enviar Resposta:
                    </label>
                    <div className="flex gap-2">
                      <textarea
                        value={resposta}
                        onChange={(e) => setResposta(e.target.value)}
                        placeholder="Digite sua resposta..."
                        className="flex-1 p-3 bg-[#252525] border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-[#CF0D1D] text-white min-h-[100px] resize-vertical"
                        disabled={enviandoResposta}
                      />
                      <Button
                        onClick={() => handleEnviarResposta(mensagens[mensagens.length - 1]?.id)}
                        disabled={enviandoResposta || !resposta.trim()}
                        className="bg-[#CF0D1D] hover:bg-[#a00a17] self-start"
                      >
                        {enviandoResposta ? (
                          'Enviando...'
                        ) : (
                          <>
                            <Send className="h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent className="max-w-4xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Captura de Tela</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center p-4">
            {selectedImage ? (
              <img
                src={selectedImage}
                alt="Screenshot ampliado"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
                onError={(e) => {
                  console.error('Erro ao carregar imagem:', selectedImage);
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><text x="50%" y="50%" text-anchor="middle" fill="red">Erro ao carregar imagem</text></svg>';
                }}
              />
            ) : (
              <p className="text-gray-500">Não foi possível carregar a imagem.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSuporteNovo;
